import leadsWidgetService from '@/modules/widgets/leadsWidget/services/leadsWidgetService';
import logError from '@/utils/logError/logError';

const DEFAULT_PERIOD = 'year';
const DEFAULT_CHART_PERIOD = 'month';
const AI_RECEPTIONIST_PATH = '/online-presence/web-ai-widget';

const emptyAiReceptionistMetrics = () => ({
  ai_receptionist_chats: 0,
  show_ai_receptionist_metrics: false,
  ai_receptionist_metrics_source: 'unavailable',
  ai_receptionist_path: AI_RECEPTIONIST_PATH,
});

const normalizeAiReceptionistMetrics = (metrics = {}) => ({
  ai_receptionist_chats: metrics.installed ? metrics.chats_count || 0 : 0,
  show_ai_receptionist_metrics: true,
  ai_receptionist_metrics_source: metrics.installed ? 'api' : 'not_installed',
  ai_receptionist_path: AI_RECEPTIONIST_PATH,
});

const emptyLeadsWidgetData = (period = DEFAULT_PERIOD, chartPeriod = DEFAULT_CHART_PERIOD) => ({
  period,
  chart_period: chartPeriod,
  range: {
    start_date: null,
    end_date: null,
  },
  totals: {
    new_leads: 0,
    new_leads_trend_percent: null,
  },
  sources: [],
  buckets: [],
  ...emptyAiReceptionistMetrics(),
});

const normalizeLeadsWidgetData = (
  data = {},
  period = DEFAULT_PERIOD,
  chartPeriod = DEFAULT_CHART_PERIOD,
  aiReceptionistMetrics = null,
) => ({
  ...emptyLeadsWidgetData(period, chartPeriod),
  ...data,
  period: data.period || period,
  chart_period: data.chart_period || chartPeriod,
  range: {
    ...emptyLeadsWidgetData(period, chartPeriod).range,
    ...(data.range || {}),
  },
  totals: {
    ...emptyLeadsWidgetData(period, chartPeriod).totals,
    ...(data.totals || {}),
  },
  sources: Array.isArray(data.sources) ? data.sources : [],
  buckets: Array.isArray(data.buckets) ? data.buckets : [],
  ...(aiReceptionistMetrics
    ? normalizeAiReceptionistMetrics(aiReceptionistMetrics)
    : emptyAiReceptionistMetrics()),
});

const state = {
  leadsWidgetData: emptyLeadsWidgetData(),
  chartBuckets: [],
  selectedPeriod: DEFAULT_PERIOD,
  selectedChartPeriod: DEFAULT_CHART_PERIOD,
  isLoading: false,
};

const getters = {
  leadsWidgetData: (state) => state.leadsWidgetData,
  chartBuckets: (state) => state.chartBuckets,
  selectedPeriod: (state) => state.selectedPeriod,
  selectedChartPeriod: (state) => state.selectedChartPeriod,
  isLoading: (state) => state.isLoading,
  showAiReceptionistMetrics: (state) => Boolean(state.leadsWidgetData?.show_ai_receptionist_metrics),
};

const mutations = {
  setLeadsWidgetData(state, data) {
    state.leadsWidgetData = data;
  },
  setChartBuckets(state, buckets) {
    state.chartBuckets = buckets;
  },
  setSelectedPeriod(state, period) {
    state.selectedPeriod = period || DEFAULT_PERIOD;
  },
  setSelectedChartPeriod(state, period) {
    state.selectedChartPeriod = period || DEFAULT_CHART_PERIOD;
  },
  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
};

const actions = {
  async fetchLeadsWidgetData({ commit, state }, { period, chartPeriod } = {}) {
    const selectedPeriod = period || state.selectedPeriod || DEFAULT_PERIOD;
    const selectedChartPeriod = chartPeriod || state.selectedChartPeriod || DEFAULT_CHART_PERIOD;
    commit('setSelectedPeriod', selectedPeriod);
    commit('setSelectedChartPeriod', selectedChartPeriod);
    commit('setIsLoading', true);

    try {
      const [response, aiReceptionistMetricsResponse] = await Promise.all([
        leadsWidgetService.getLeadsWidgetData(selectedPeriod, selectedChartPeriod),
        leadsWidgetService.getAiReceptionistMetrics(selectedPeriod).catch((error) => {
          logError('There was an error retrieving AI Receptionist metrics', error);
          return null;
        }),
      ]);

      if (response?.success) {
        const normalizedData = normalizeLeadsWidgetData(
          response.data,
          selectedPeriod,
          selectedChartPeriod,
          aiReceptionistMetricsResponse?.success ? aiReceptionistMetricsResponse.data : null,
        );
        commit('setLeadsWidgetData', normalizedData);
        commit('setChartBuckets', normalizedData.buckets);
        return true;
      }

      commit('setLeadsWidgetData', emptyLeadsWidgetData(selectedPeriod, selectedChartPeriod));
      commit('setChartBuckets', []);
      return false;
    } catch (error) {
      logError('There was an error retrieving leads widget data', error);
      commit('setLeadsWidgetData', emptyLeadsWidgetData(selectedPeriod, selectedChartPeriod));
      commit('setChartBuckets', []);
      return false;
    } finally {
      commit('setIsLoading', false);
    }
  },
};

export {
  DEFAULT_PERIOD,
  DEFAULT_CHART_PERIOD,
  emptyAiReceptionistMetrics,
  emptyLeadsWidgetData,
  normalizeAiReceptionistMetrics,
  normalizeLeadsWidgetData,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
